import React, { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Plus, 
  Search, 
  Trash2, 
  Target,
  ChevronRight,
  ChevronDown,
  X,
  AlertCircle,
  CheckCircle,
  Settings,
  Users,
  Briefcase,
  Lock
} from 'lucide-react';
import toast from 'react-hot-toast';
import staffExpertiseService from '../../services/staffExpertiseService';
import categoryService from '../../services/categoryService';

const checkCategoryStatus = (categoryId, categories, existingExpertiseIds, currentExpertise) => {
  if (existingExpertiseIds.has(categoryId)) {
    return {
      type: 'duplicate',
      message: 'You already have expertise in this category.'
    };
  }

  const findCategory = (cats, id) => {
    for (const cat of cats) {
      if (cat.id === id) return cat;
      if (cat.children) {
        const found = findCategory(cat.children, id);
        if (found) return found;
      }
    }
    return null;
  };

  const category = findCategory(categories, categoryId);
  if (!category) return { type: 'valid' };

  const hasParentExpertise = currentExpertise.some(exp => {
    return category.full_path && category.full_path.startsWith(exp.category.full_path + ' > ');
  });

  if (hasParentExpertise) {
    const parentExpertise = currentExpertise.find(exp => {
      return category.full_path && category.full_path.startsWith(exp.category.full_path + ' > ');
    });
    return {
      type: 'info',
      message: `Note: You already have expertise in "${parentExpertise.category.name}" which covers this category.`
    };
  }

  const hasChildExpertise = currentExpertise.some(exp => {
    return exp.category.full_path && exp.category.full_path.startsWith(category.full_path + ' > ');
  });

  if (hasChildExpertise) {
    const childCount = currentExpertise.filter(exp => {
      return exp.category.full_path && exp.category.full_path.startsWith(category.full_path + ' > ');
    }).length;
    
    return {
      type: 'info',
      message: `Note: Adding this parent category will consolidate your ${childCount} existing subcategory expertise.`
    };
  }

  return { type: 'valid' };
};

const StaffExpertiseManagement = ({ userId, userName }) => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [expertiseToDelete, setExpertiseToDelete] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const queryClient = useQueryClient();

  // Fetch user's current expertise
  const { data: expertiseData, isLoading: expertiseLoading } = useQuery({
    queryKey: ['staff-expertise', userId],
    queryFn: () => staffExpertiseService.getStaffExpertise(userId),
    enabled: !!userId
  });

  // Fetch available categories for adding new expertise
  const { data: categoriesData } = useQuery({
    queryKey: ['categories-tree'],
    queryFn: () => categoryService.getCategoryTree()
  });

  // Add expertise mutation
  const addExpertiseMutation = useMutation({
    mutationFn: (data) => {
      console.log('Sending expertise data:', data);
      return staffExpertiseService.addStaffExpertise(userId, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['staff-expertise', userId]);
      setShowAddModal(false);
      toast.success('Expertise added successfully');
    },
    onError: (error) => {
      console.error('Add expertise error:', error);
      const errorMessage = error?.response?.data?.message || error.message || 'Failed to add expertise';
      toast.error(errorMessage);
    }
  });

  // Delete expertise mutation
  const deleteExpertiseMutation = useMutation({
    mutationFn: (expertiseId) => 
      staffExpertiseService.removeStaffExpertise(userId, expertiseId),
    onSuccess: () => {
      queryClient.invalidateQueries(['staff-expertise', userId]);
      setShowDeleteModal(false);
      setExpertiseToDelete(null);
      toast.success('Expertise removed successfully');
    },
    onError: (error) => {
      console.error('Delete expertise error:', error);
      
      // Handle 404 errors specifically
      if (error.response?.status === 404) {
        toast.error('This expertise has already been removed or does not exist');
        // Still invalidate to refresh the data
        queryClient.invalidateQueries(['staff-expertise', userId]);
      } else {
        const errorMessage = error?.response?.data?.message || error.message || 'Failed to remove expertise';
        toast.error(errorMessage);
      }
      
      setShowDeleteModal(false);
      setExpertiseToDelete(null);
    }
  });

  const userExpertise = expertiseData?.data?.expertise || [];
  const userInfo = expertiseData?.data?.user || {};
  const summary = expertiseData?.data?.summary || {};
  const categoryTree = categoriesData?.data?.categories || [];

  // Build a tree structure for display
  const buildExpertiseTree = (expertise) => {
    const expertiseMap = {};
    const validExpertise = expertise.filter(exp => exp && exp.category); // Filter out invalid entries
    
    validExpertise.forEach(exp => {
      expertiseMap[exp.category.id] = exp;
    });

    const tree = [];
    const processed = new Set();

    validExpertise.forEach(exp => {
      if (processed.has(exp.category.id) || !exp.category) return;

      const hasParentInList = validExpertise.some(other => 
        other.category.id !== exp.category.id && 
        isParentCategory(other.category, exp.category)
      );

      if (!hasParentInList) {
        tree.push(buildExpertiseSubtree(exp, expertiseMap, processed));
      }
    });

    return tree;
  };

  const buildExpertiseSubtree = (expertise, expertiseMap, processed) => {
    processed.add(expertise.category.id);
    
    const children = Object.values(expertiseMap).filter(other => 
      !processed.has(other.category.id) && 
      isParentCategory(expertise.category, other.category)
    );

    return {
      ...expertise,
      children: children.map(child => buildExpertiseSubtree(child, expertiseMap, processed))
    };
  };

  const isParentCategory = (parent, child) => {
    return child.full_path && child.full_path.startsWith(parent.full_path + ' > ');
  };

  const handleAddExpertise = (data) => {
    const validatedData = {
      category_id: parseInt(data.category_id),
      max_concurrent_tickets: Math.max(1, Math.min(50, parseInt(data.max_concurrent_tickets) || 10)),
      notes: (data.notes || '').trim(),
      auto_add_subcategories: true
    };
    
    if (!validatedData.category_id || isNaN(validatedData.category_id)) {
      toast.error('Valid category ID is required');
      return;
    }
    
    console.log('Validated data before sending:', validatedData);
    addExpertiseMutation.mutate(validatedData);
  };

  const handleDeleteExpertise = () => {
    if (expertiseToDelete) {
      deleteExpertiseMutation.mutate(expertiseToDelete.id);
    }
  };

  if (expertiseLoading) {
    return (
      <div className="p-8 text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
        <p className="mt-2 text-gray-600">Loading expertise data...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with User Info */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center">
              <Users className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="text-xl font-semibold text-gray-900">{userName}</h3>
              <div className="flex items-center space-x-4 mt-1">
                <span className="px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full">
                  {userInfo.staff_level_name || `Level ${userInfo.staff_level}`}
                </span>
                <span className="text-sm text-gray-600">
                  Current Workload: {userInfo.current_workload || 0} tickets
                </span>
                <span className="text-sm text-gray-600">
                  Expertise Areas: {userInfo.total_expertise_areas || 0}
                </span>
              </div>
            </div>
          </div>
          <button
            onClick={() => setShowAddModal(true)}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm font-medium"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Expertise
          </button>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="p-6 border-b border-gray-200">
          <h4 className="text-lg font-medium text-gray-900">Performance Overview</h4>
        </div>
        
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-4 rounded-lg border border-blue-200">
              <div className="flex items-center">
                <Target className="w-8 h-8 text-blue-600 mr-3" />
                <div>
                  <p className="text-sm text-blue-600">Total Categories</p>
                  <p className="text-2xl font-bold text-blue-900">
                    {userInfo.total_expertise_areas || 0}
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-gradient-to-r from-green-50 to-green-100 p-4 rounded-lg border border-green-200">
              <div className="flex items-center">
                <CheckCircle className="w-8 h-8 text-green-600 mr-3" />
                <div>
                  <p className="text-sm text-green-600">Total Completed</p>
                  <p className="text-2xl font-bold text-green-900">
                    {summary.total_tickets_completed || 0}
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-gradient-to-r from-purple-50 to-purple-100 p-4 rounded-lg border border-purple-200">
              <div className="flex items-center">
                <Briefcase className="w-8 h-8 text-purple-600 mr-3" />
                <div>
                  <p className="text-sm text-purple-600">Forwarded Received</p>
                  <p className="text-2xl font-bold text-purple-900">
                    {summary.forwarded_received_count || 0}
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-gradient-to-r from-orange-50 to-orange-100 p-4 rounded-lg border border-orange-200">
              <div className="flex items-center">
                <Settings className="w-8 h-8 text-orange-600 mr-3" />
                <div>
                  <p className="text-sm text-orange-600">Forwarded Out</p>
                  <p className="text-2xl font-bold text-orange-900">
                    {summary.forwarded_sent_count || 0}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Current Expertise - Tree View */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h4 className="text-lg font-medium text-gray-900">Current Expertise Areas</h4>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search expertise..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
        </div>

        <div className="p-6">
          {userExpertise.length === 0 ? (
            <div className="text-center py-8">
              <Target className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 text-lg">No expertise areas assigned</p>
              <p className="text-gray-500 text-sm mt-2">
                Click "Add Expertise" to assign categories to this staff member
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {buildExpertiseTree(userExpertise).map(expertise => (
                <ExpertiseTreeItem
                  key={expertise.id}
                  expertise={expertise}
                  level={0}
                  onDelete={(exp) => {
                    setExpertiseToDelete(exp);
                    setShowDeleteModal(true);
                  }}
                  searchTerm={searchTerm}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Add Expertise Modal */}
      {showAddModal && (
        <AddExpertiseModal
          categories={categoryTree}
          currentExpertise={userExpertise}
          onSubmit={handleAddExpertise}
          onClose={() => setShowAddModal(false)}
          isLoading={addExpertiseMutation.isLoading}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && expertiseToDelete && (
        <DeleteExpertiseModal
          expertise={expertiseToDelete}
          onConfirm={handleDeleteExpertise}
          onClose={() => {
            setShowDeleteModal(false);
            setExpertiseToDelete(null);
          }}
          isLoading={deleteExpertiseMutation.isLoading}
        />
      )}
    </div>
  );
};

// Expertise Tree Item Component 
const ExpertiseTreeItem = ({ expertise, level, onDelete, searchTerm }) => {
  const [expanded, setExpanded] = useState(true);
  
  const isMatch = !searchTerm || 
    expertise.category.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    expertise.category.full_path.toLowerCase().includes(searchTerm.toLowerCase());

  const hasMatchingChildren = expertise.children?.some(child => 
    child.category.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    child.category.full_path.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (!isMatch && !hasMatchingChildren) {
    return null;
  }

  return (
    <div className="border border-gray-200 rounded-lg mb-2">
      <div 
        className={`p-4 hover:bg-gray-50 transition-colors ${level > 0 ? 'bg-gray-25' : ''}`}
        style={{ paddingLeft: `${16 + level * 24}px` }}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center flex-1">
            {expertise.children?.length > 0 && (
              <button
                onClick={() => setExpanded(!expanded)}
                className="mr-2 w-5 h-5 flex items-center justify-center text-gray-500 hover:text-gray-700"
              >
                {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              </button>
            )}
            
            <div className="flex-1">
              <div className="flex items-center space-x-3">
                <h5 className="font-medium text-gray-900">{expertise.category.name}</h5>
                
                <span className={`px-2 py-0.5 text-xs rounded-full ${
                  expertise.category.level === 0 
                    ? 'bg-blue-100 text-blue-800' 
                    : expertise.category.level === 1 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-gray-100 text-gray-600'
                }`}>
                  L{expertise.category.level}
                </span>

                {expertise.performance_stats && (
                  <>
                    {expertise.performance_stats.total_completed > 0 && (
                      <span className="px-2 py-0.5 text-xs bg-green-100 text-green-800 rounded-full">
                        {expertise.performance_stats.total_completed} completed
                      </span>
                    )}
                    {expertise.performance_stats.forwarded_received > 0 && (
                      <span className="px-2 py-0.5 text-xs bg-purple-100 text-purple-800 rounded-full">
                        ↓ {expertise.performance_stats.forwarded_received} received
                      </span>
                    )}
                    {expertise.performance_stats.forwarded_sent > 0 && (
                      <span className="px-2 py-0.5 text-xs bg-orange-100 text-orange-800 rounded-full">
                        ↑ {expertise.performance_stats.forwarded_sent} sent
                      </span>
                    )}
                  </>
                )}
              </div>
              
              <p className="text-sm text-gray-600 mt-1">{expertise.category.full_path}</p>
              
              <div className="flex items-center space-x-4 mt-2 text-sm text-gray-500">
                <span>Max Tickets: {expertise.max_concurrent_tickets}</span>
                <span>Current: {expertise.current_category_workload || 0}</span>
                <span>Available: {expertise.available_capacity || expertise.max_concurrent_tickets}</span>
                {expertise.performance_stats && (
                  <span className="text-blue-600">
                    Completed: {expertise.performance_stats.total_completed || 0}
                  </span>
                )}
              </div>

              {expertise.notes && (
                <p className="text-sm text-gray-600 mt-2 italic">"{expertise.notes}"</p>
              )}
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <button
              onClick={() => onDelete(expertise)}
              className="text-red-600 hover:text-red-900 p-1"
              title="Remove expertise"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
      
      {expanded && expertise.children?.length > 0 && (
        <div className="border-t border-gray-100">
          {expertise.children.map(child => (
            <ExpertiseTreeItem
              key={child.id}
              expertise={child}
              level={level + 1}
              onDelete={onDelete}
              searchTerm={searchTerm}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// Add Expertise Modal 
const AddExpertiseModal = ({ categories, currentExpertise, onSubmit, onClose, isLoading }) => {
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [formData, setFormData] = useState({
    max_concurrent_tickets: 10,
    notes: ''
  });
  const [errors, setErrors] = useState({});
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [submissionStatus, setSubmissionStatus] = useState(null);
  
  const MAX_TICKETS_CONFIG = {
    default: 10,
    min: 1,
    max: 50
  };

  const existingExpertiseIds = new Set(
    currentExpertise.map(exp => exp.category.id)
  );

  const handleMaxTicketsChange = (e) => {
    const value = e.target.value;
    
    if (value === '') {
      setFormData(prev => ({ 
        ...prev, 
        max_concurrent_tickets: '' 
      }));
      return;
    }
    
    let numericValue = parseInt(value);
    
    if (!isNaN(numericValue)) {
      numericValue = Math.max(MAX_TICKETS_CONFIG.min, Math.min(MAX_TICKETS_CONFIG.max, numericValue));
      setFormData(prev => ({ 
        ...prev, 
        max_concurrent_tickets: numericValue 
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const newErrors = {};
    
    if (selectedCategories.length === 0) {
      newErrors.categories = 'At least one category is required';
      setErrors(newErrors);
      return;
    }

    const maxTickets = formData.max_concurrent_tickets === '' ? 
      MAX_TICKETS_CONFIG.default : 
      parseInt(formData.max_concurrent_tickets);
    
    if (maxTickets < MAX_TICKETS_CONFIG.min || maxTickets > MAX_TICKETS_CONFIG.max) {
      newErrors.max_concurrent_tickets = `Must be between ${MAX_TICKETS_CONFIG.min} and ${MAX_TICKETS_CONFIG.max}`;
      setErrors(newErrors);
      return;
    }

    const categoriesToAdd = getOptimalCategorySelection();
    
    console.log('Original categories:', selectedCategories);
    console.log('Filtered categories:', categoriesToAdd);
    
    if (categoriesToAdd.length === 0) {
      toast.error('All selected categories are already covered by existing expertise');
      return;
    }

    try {
      // Process each optimal category selection
      for (const categoryId of categoriesToAdd) {
        const submissionData = {
          category_id: categoryId,
          max_concurrent_tickets: maxTickets,
          notes: formData.notes || '',
          auto_add_subcategories: true
        };

        console.log('Submitting category with data:', submissionData);
        await onSubmit(submissionData);
      }
      
      toast.success(`Added ${categoriesToAdd.length} parent categories successfully`);
      setSelectedCategories([]);
      setFormData({ max_concurrent_tickets: 10, notes: '' });
      onClose();
      
    } catch (error) {
      console.error('Submission error:', error);
    }
  };

  // HELPER FUNCTION, AddExpertiseModal COMPONENT
  const getOptimalCategorySelection = () => {
    const flattenCategories = (cats, result = []) => {
      cats.forEach(cat => {
        result.push(cat);
        if (cat.children && cat.children.length > 0) {
          flattenCategories(cat.children, result);
        }
      });
      return result;
    };

    const flatCategories = flattenCategories(categories);
    const selectedCategoryObjects = selectedCategories.map(id => 
      flatCategories.find(cat => cat.id === id)
    ).filter(Boolean);

    // Remove child categories if their parent is also selected
    const optimalSelection = selectedCategoryObjects.filter(category => {
      // Check if any selected category is a parent of this category
      const hasParentSelected = selectedCategoryObjects.some(otherCategory => {
        if (otherCategory.id === category.id) return false;
        
        // Check if current category's path starts with other category's path
        if (category.full_path && otherCategory.full_path) {
          return category.full_path.startsWith(otherCategory.full_path + ' > ');
        }
        
        // Fallback: check direct parent relationship
        return category.parent_id === otherCategory.id;
      });
      
      return !hasParentSelected;
    });

    console.log('Selection filtering:', {
      original: selectedCategories,
      objects: selectedCategoryObjects.map(c => ({ id: c.id, name: c.name, path: c.full_path })),
      filtered: optimalSelection.map(c => ({ id: c.id, name: c.name, path: c.full_path }))
    });

    return optimalSelection.map(cat => cat.id);
  };


  const handleCategorySelection = (categoryIds) => {
    console.log('Categories selected:', categoryIds);
    setSelectedCategories(categoryIds);
    setShowCategoryModal(false);
  };

  const removeCategory = (categoryIdToRemove) => {
    setSelectedCategories(prev => prev.filter(id => id !== categoryIdToRemove));
  };

  const getSelectedCategoryNames = () => {
    const flattenCategories = (cats, result = []) => {
      cats.forEach(cat => {
        result.push(cat);
        if (cat.children && cat.children.length > 0) {
          flattenCategories(cat.children, result);
        }
      });
      return result;
    };

    const flatCategories = flattenCategories(categories);
    return selectedCategories.map(id => {
      const category = flatCategories.find(cat => cat.id === id);
      if (category) {
        return {
          ...category,
          status: checkCategoryStatus(id, categories, existingExpertiseIds, currentExpertise)
        };
      }
      return null;
    }).filter(Boolean);
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 z-50">
      <div className="flex items-center justify-center min-h-screen p-4">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
          <div className="p-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-semibold text-gray-900">Add Category Expertise</h3>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>

            {submissionStatus && (
              <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-blue-800">
                    Submitting Categories
                  </span>
                  <span className="text-sm text-blue-600">
                    {submissionStatus.completed}/{submissionStatus.total}
                  </span>
                </div>
                <div className="w-full bg-blue-200 rounded-full h-2">
                  <div 
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${(submissionStatus.completed / submissionStatus.total) * 100}%` }}
                  ></div>
                </div>
                {submissionStatus.errors.length > 0 && (
                  <div className="mt-2">
                    {submissionStatus.errors.map((error, index) => (
                      <p key={index} className="text-xs text-red-600">{error}</p>
                    ))}
                  </div>
                )}
              </div>
            )}
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Categories *
                </label>
                <div className="space-y-3">
                  {selectedCategories.length === 0 ? (
                    <button
                      type="button"
                      onClick={() => setShowCategoryModal(true)}
                      className="w-full p-4 border-2 border-dashed border-gray-300 rounded-md text-gray-600 hover:border-gray-400 hover:text-gray-800 flex items-center justify-center"
                    >
                      <Plus className="w-5 h-5 mr-2" />
                      Click to select categories
                    </button>
                  ) : (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-700">
                          Selected Categories ({selectedCategories.length})
                        </span>
                        <button
                          type="button"
                          onClick={() => setShowCategoryModal(true)}
                          className="text-blue-600 hover:text-blue-800 text-sm"
                        >
                          + Add More
                        </button>
                      </div>
                      <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-md p-2 bg-gray-50">
                        {getSelectedCategoryNames().map(category => (
                          <div key={category.id} className={`flex items-center justify-between p-2 rounded border mb-2 last:mb-0 ${
                            category.status.type === 'duplicate' ? 'bg-red-50 border-red-200' :
                            category.status.type === 'info' ? 'bg-blue-50 border-blue-200' : 'bg-white'
                          }`}>
                            <div className="flex-1">
                              <div className="flex items-center space-x-2">
                                <span className="font-medium text-gray-900">{category.name}</span>
                                {category.status.type === 'duplicate' && (
                                  <Lock className="w-4 h-4 text-red-500" title="Already exists" />
                                )}
                                {category.status.type === 'info' && (
                                  <AlertCircle className="w-4 h-4 text-blue-500" title="Information" />
                                )}
                              </div>
                              <p className="text-sm text-gray-500">{category.full_path}</p>
                              {category.status.type !== 'valid' && (
                                <p className={`text-xs mt-1 ${
                                  category.status.type === 'duplicate' ? 'text-red-600' :
                                  category.status.type === 'info' ? 'text-blue-600' : 'text-gray-600'
                                }`}>{category.status.message}</p>
                              )}
                            </div>
                            <button
                              type="button"
                              onClick={() => removeCategory(category.id)}
                              className="text-red-600 hover:text-red-800 ml-2"
                              title="Remove category"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                {errors.categories && <p className="text-red-600 text-sm mt-1">{errors.categories}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Max Concurrent Tickets *
                </label>
                <input
                  type="number"
                  min={MAX_TICKETS_CONFIG.min}
                  max={MAX_TICKETS_CONFIG.max}
                  value={formData.max_concurrent_tickets}
                  onChange={handleMaxTicketsChange}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder={`Enter value between ${MAX_TICKETS_CONFIG.min} and ${MAX_TICKETS_CONFIG.max}`}
                />
                {errors.max_concurrent_tickets && <p className="text-red-600 text-sm mt-1">{errors.max_concurrent_tickets}</p>}
                <p className="text-xs text-gray-500 mt-1">
                  This setting will be applied to all selected categories. 
                  Default: {MAX_TICKETS_CONFIG.default}, Range: {MAX_TICKETS_CONFIG.min}-{MAX_TICKETS_CONFIG.max}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes (Optional)
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  rows={3}
                  maxLength={500}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Any additional notes about this expertise assignment..."
                />
                <p className="text-xs text-gray-500 mt-1">
                  {formData.notes.length}/500 characters
                </p>
              </div>

              <div className="flex items-center justify-end space-x-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 border border-red-300 rounded-md text-sm font-medium text-red-700 hover:bg-red-50 bg-white"
                  disabled={isLoading || submissionStatus}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isLoading || selectedCategories.length === 0 || submissionStatus}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 flex items-center"
                >
                  {(isLoading || submissionStatus) && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />}
                  {submissionStatus ? 'Adding...' : `Add ${selectedCategories.length > 1 ? `${selectedCategories.length} Categories` : 'Expertise'}`}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {showCategoryModal && (
        <EnhancedCategorySelectionModal
          categories={categories}
          onSelect={handleCategorySelection}
          onClose={() => setShowCategoryModal(false)}
          initialSelected={selectedCategories}
          existingExpertise={currentExpertise}
        />
      )}
    </div>
  );
};

// Delete Confirmation Modal
const DeleteExpertiseModal = ({ expertise, onConfirm, onClose, isLoading }) => {
  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 z-50">
      <div className="flex items-center justify-center min-h-screen p-4">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
          <div className="p-6">
            <div className="flex items-center mb-4">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                <AlertCircle className="h-6 w-6 text-red-600" />
              </div>
            </div>
            <div className="text-center">
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Remove Expertise
              </h3>
              <p className="text-sm text-gray-500">
                Are you sure you want to remove expertise in{' '}
                <strong>{expertise.category.name}</strong>? This action cannot be undone.
              </p>
            </div>
            <div className="mt-6 flex items-center justify-center space-x-3">
              <button
                onClick={onClose}
                className="px-4 py-2 border border-red-300 rounded-md text-sm font-medium text-red-700 hover:bg-red-50 bg-white"
              >
                Cancel
              </button>
              <button
                onClick={onConfirm}
                disabled={isLoading}
                className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700 disabled:opacity-50"
              >
                {isLoading ? 'Removing...' : 'Remove'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Enhanced Category Selection Modal
const EnhancedCategorySelectionModal = ({ categories, onSelect, onClose, initialSelected = [], existingExpertise = [] }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedCategories, setExpandedCategories] = useState(new Set(['all_root']));
  const [selectedCategories, setSelectedCategories] = useState(new Set(initialSelected));

  const existingExpertiseIds = new Set(
    existingExpertise.map(exp => exp.category.id)
  );

  const getCategoryStatus = (categoryId) => {
    if (existingExpertiseIds.has(categoryId)) {
      return 'existing';
    }
    return 'available';
  };

  const filterCategories = (cats, term) => {
    if (!term) return cats;
    
    return cats.filter(cat => {
      const matchesName = cat.name.toLowerCase().includes(term.toLowerCase());
      const matchesDescription = cat.description && cat.description.toLowerCase().includes(term.toLowerCase());
      const hasMatchingChildren = cat.children && cat.children.length > 0 && filterCategories(cat.children, term).length > 0;
      
      return matchesName || matchesDescription || hasMatchingChildren;
    }).map(cat => ({
      ...cat,
      children: cat.children ? filterCategories(cat.children, term) : []
    }));
  };

  const filteredCategories = filterCategories(categories, searchTerm);

  const getAllDescendantIds = (categoryId, categories) => {
    let descendantIds = [];
    
    const findAndCollectDescendants = (cats) => {
      for (const cat of cats) {
        if (cat.id === categoryId) {
          const collectChildren = (category) => {
            if (category.children && category.children.length > 0) {
              category.children.forEach(child => {
                descendantIds.push(child.id);
                collectChildren(child);
              });
            }
          };
          collectChildren(cat);
          return true;
        }
        if (cat.children && cat.children.length > 0) {
          if (findAndCollectDescendants(cat.children)) {
            return true;
          }
        }
      }
      return false;
    };

    findAndCollectDescendants(categories);
    return descendantIds;
  };

  const handleCategoryToggle = (categoryId) => {
    if (existingExpertiseIds.has(categoryId)) {
      toast.error('This category already has expertise assigned.');
      return;
    }

    const newSelected = new Set(selectedCategories);
    const descendantIds = getAllDescendantIds(categoryId, categories);
    
    if (newSelected.has(categoryId)) {
      newSelected.delete(categoryId);
      descendantIds.forEach(id => {
        if (!existingExpertiseIds.has(id)) {
          newSelected.delete(id);
        }
      });
    } else {
      newSelected.add(categoryId);
      descendantIds.forEach(id => {
        if (!existingExpertiseIds.has(id)) {
          newSelected.add(id);
        }
      });
      
      const availableDescendants = descendantIds.filter(id => !existingExpertiseIds.has(id));
      if (availableDescendants.length > 0) {
        toast.success(
          `Selected category with ${availableDescendants.length} subcategories. All will be added automatically.`,
          { duration: 3000 }
        );
      }
    }
    
    setSelectedCategories(newSelected);
  };

  const toggleExpanded = (categoryId) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(categoryId)) {
      newExpanded.delete(categoryId);
    } else {
      newExpanded.add(categoryId);
    }
    setExpandedCategories(newExpanded);
  };

  const isIndeterminate = (categoryId) => {
    const descendantIds = getAllDescendantIds(categoryId, categories);
    if (descendantIds.length === 0) return false;
    
    const availableDescendants = descendantIds.filter(id => !existingExpertiseIds.has(id));
    const selectedDescendants = availableDescendants.filter(id => selectedCategories.has(id));
    
    return selectedDescendants.length > 0 && selectedDescendants.length < availableDescendants.length;
  };

  const isFullySelected = (categoryId) => {
    if (existingExpertiseIds.has(categoryId)) return false;
    if (selectedCategories.has(categoryId)) return true;
    
    const descendantIds = getAllDescendantIds(categoryId, categories);
    if (descendantIds.length === 0) return false;
    
    const availableDescendants = descendantIds.filter(id => !existingExpertiseIds.has(id));
    return availableDescendants.length > 0 && availableDescendants.every(id => selectedCategories.has(id));
  };

  useEffect(() => {
    if (searchTerm) {
      const expandAll = (cats) => {
        const ids = [];
        cats.forEach(cat => {
          ids.push(cat.id);
          if (cat.children && cat.children.length > 0) {
            ids.push(...expandAll(cat.children));
          }
        });
        return ids;
      };
      setExpandedCategories(new Set(['all_root', ...expandAll(filteredCategories)]));
    }
  }, [searchTerm, filteredCategories]);

  const handleConfirmSelection = () => {
    const selectedArray = Array.from(selectedCategories);
    onSelect(selectedArray);
  };

  const selectAllAvailable = () => {
    const allAvailableIds = [];
    const collectAvailableIds = (cats) => {
      cats.forEach(cat => {
        if (!existingExpertiseIds.has(cat.id)) {
          allAvailableIds.push(cat.id);
        }
        if (cat.children && cat.children.length > 0) {
          collectAvailableIds(cat.children);
        }
      });
    };
    collectAvailableIds(categories);
    setSelectedCategories(new Set(allAvailableIds));
  };

  const clearAll = () => {
    setSelectedCategories(new Set());
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 z-50">
      <div className="flex items-center justify-center min-h-screen p-4">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] overflow-hidden">
          <div className="p-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-semibold text-gray-900">
                Select Categories & Subcategories
              </h3>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="space-y-4 mb-6">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search categories and subcategories..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="flex space-x-2">
                  <button
                    onClick={selectAllAvailable}
                    className="px-3 py-1 bg-blue-100 text-blue-700 rounded text-sm hover:bg-blue-200"
                  >
                    Select All Available
                  </button>
                  <button
                    onClick={clearAll}
                    className="px-3 py-1 bg-gray-100 text-gray-700 rounded text-sm hover:bg-gray-200"
                  >
                    Clear All
                  </button>
                </div>
                <div className="text-sm text-gray-600">
                  <span className="font-medium">{selectedCategories.size}</span> categories selected
                </div>
              </div>
            <div className="flex justify-end space-x-2">
              <button
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmSelection}
                disabled={selectedCategories.size === 0}
                className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Add {selectedCategories.size} Categories
              </button>
            </div>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
            <p className="text-sm text-blue-800">
              <strong>Automatic Hierarchy Management:</strong> When you select a parent category, all its available subcategories will be automatically included. 
              When you delete a parent category, all its subcategories will also be removed. Categories with existing expertise are marked with a lock icon.
            </p>
            </div>
            
            <div className="border rounded-lg overflow-hidden bg-gray-50 max-h-96 overflow-y-auto">
              {filteredCategories.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  {searchTerm ? 'No categories found matching your search.' : 'No categories available.'}
                </div>
              ) : (
                <div className="divide-y divide-gray-200">
                  {filteredCategories.map(category => (
                    <EnhancedCategoryTreeItem
                      key={category.id}
                      category={category}
                      level={0}
                      selectedCategories={selectedCategories}
                      expandedCategories={expandedCategories}
                      onToggleCategory={handleCategoryToggle}
                      onToggleExpanded={toggleExpanded}
                      isIndeterminate={isIndeterminate}
                      isFullySelected={isFullySelected}
                      getCategoryStatus={getCategoryStatus}
                    />
                  ))}
                </div>
              )}
            </div>

            <div className="mt-6 flex items-center justify-between">
              <div className="text-sm text-gray-600">
                {selectedCategories.size > 0 && (
                  <span>
                    Selected: <strong>{selectedCategories.size}</strong> categories
                  </span>
                )}
              </div>
                {/* <div className="flex space-x-2">
                  <button
                    onClick={onClose}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleConfirmSelection}
                    disabled={selectedCategories.size === 0}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Add {selectedCategories.size} Categories
                  </button>
                </div> */}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Enhanced Category Tree Item
const EnhancedCategoryTreeItem = ({ 
  category, 
  level, 
  selectedCategories, 
  expandedCategories, 
  onToggleCategory, 
  onToggleExpanded,
  isIndeterminate,
  isFullySelected,
  getCategoryStatus
}) => {
  const hasChildren = category.children && category.children.length > 0;
  const isExpanded = expandedCategories.has(category.id) || expandedCategories.has('all_root');
  const isSelected = selectedCategories.has(category.id);
  const indeterminate = isIndeterminate(category.id);
  const fullySelected = isFullySelected(category.id);
  const categoryStatus = getCategoryStatus(category.id);
  const isExisting = categoryStatus === 'existing';

  const checkboxRef = useRef(null);
  
  useEffect(() => {
    if (checkboxRef.current) {
      checkboxRef.current.indeterminate = indeterminate && !isSelected;
    }
  }, [indeterminate, isSelected]);

  const getLevelColor = (level) => {
    const colors = [
      'border-l-blue-500 bg-blue-50',
      'border-l-green-500 bg-green-50',
      'border-l-purple-500 bg-purple-50',
      'border-l-orange-500 bg-orange-50',
    ];
    return colors[Math.min(level, colors.length - 1)];
  };

  const getTextColor = (level, isExisting) => {
    if (isExisting) return 'text-gray-500';
    return level === 0 ? 'text-gray-900 font-semibold' : 
           level === 1 ? 'text-gray-800 font-medium' : 
           'text-gray-700';
  };

  const getBackgroundColor = () => {
    if (isExisting) return 'bg-gray-100';
    return 'hover:bg-white transition-colors';
  };

  return (
    <>
      <div 
        className={`flex items-center p-4 ${getBackgroundColor()} border-l-4 ${getLevelColor(level)} ${
          isExisting ? 'opacity-70' : ''
        }`}
        style={{ paddingLeft: `${16 + level * 24}px` }}
      >
        {hasChildren && (
          <button
            onClick={() => onToggleExpanded(category.id)}
            className="mr-3 w-6 h-6 flex items-center justify-center text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded"
          >
            {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </button>
        )}
        
        <div className="mr-3">
          {isExisting ? (
            <div className="w-4 h-4 flex items-center justify-center">
              <Lock className="w-4 h-4 text-gray-400" />
            </div>
          ) : (
            <input
              ref={checkboxRef}
              type="checkbox"
              checked={isSelected || fullySelected}
              onChange={() => onToggleCategory(category.id)}
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
          )}
        </div>
        
        <div className="flex-1">
          <div className="flex items-center space-x-3">
            <span className={`${getTextColor(level, isExisting)}`}>
              {category.name}
            </span>

            <span className={`px-2 py-0.5 text-xs rounded-full ${
              level === 0 
                ? 'bg-blue-100 text-blue-800' 
                : level === 1 
                ? 'bg-green-100 text-green-800' 
                : level === 2
                ? 'bg-purple-100 text-purple-800'
                : 'bg-gray-100 text-gray-600'
            }`}>
              Level {level}
            </span>

            {hasChildren && (
              <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                {category.children.length} subcategories
              </span>
            )}

            {isExisting && (
              <span className="text-xs text-red-600 bg-red-100 px-2 py-0.5 rounded-full flex items-center">
                <Lock className="w-3 h-3 mr-1" />
                Already Assigned
              </span>
            )}
            
            {indeterminate && !isSelected && !isExisting && (
              <span className="text-xs text-orange-600 bg-orange-100 px-2 py-0.5 rounded-full">
                Partial
              </span>
            )}
            
            {(isSelected || fullySelected) && !isExisting && (
              <span className="text-xs text-green-600 bg-green-100 px-2 py-0.5 rounded-full">
                ✓ Selected
              </span>
            )}
          </div>
          
          {category.description && (
            <div className={`text-sm mt-1 ${isExisting ? 'text-gray-400' : 'text-gray-500'}`}>
              {category.description}
            </div>
          )}

          {level > 0 && (
            <div className={`text-xs mt-1 ${isExisting ? 'text-gray-400' : 'text-gray-400'}`}>
              Path: {category.full_path || `Level ${level} category`}
            </div>
          )}
        </div>
      </div>
      
      {isExpanded && hasChildren && (
        <div>
          {category.children.map(child => (
            <EnhancedCategoryTreeItem
              key={child.id}
              category={child}
              level={level + 1}
              selectedCategories={selectedCategories}
              expandedCategories={expandedCategories}
              onToggleCategory={onToggleCategory}
              onToggleExpanded={onToggleExpanded}
              isIndeterminate={isIndeterminate}
              isFullySelected={isFullySelected}
              getCategoryStatus={getCategoryStatus}
            />
          ))}
        </div>
      )}
    </>
  );
};

export default StaffExpertiseManagement;